import { inject, injectable } from 'inversify'
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok } from '@xlearn/common'
import * as SerializationApi from '@/app/state-serialization/api'
import { StateSerializationPlugin, StateSerializationFactory,
         stateSerializerName } from '@/app/state-serialization/api'
import { ScormSuspendDataState } from './scorm-suspend-data-state'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';
import { reportNonFatalError } from '@/app/error-handling';

@injectable()
export class Scorm2004SuspendDataProtobufSerializer implements StateSerializationPlugin<ScormSuspendDataState> {

  key = { id: 1, name: { ns: 'scorm2004-tracking', name: 'suspend-data' }, format: 'protobuf' }

  constructor(
    @inject(SerializationApi.StateSerializationFactorySymbol)
    private serializerFactory: StateSerializationFactory) {
  }

  serialize(data: ScormSuspendDataState): Result<Uint8Array, SerializationApi.SerializerError> {

    let items = []
    
    for (let item of data) {
      const name = stateSerializerName('suspend-data', item.name)
      const serializerResult = this.serializerFactory.resolveByName(name, 'protobuf')
      if (!serializerResult.success) {
        reportNonFatalError(`scorm suspend data 'protobuf' serializer not found with ` +
              ` name '${JSON.stringify(name)}' data won't be included.`)
        continue
      }

      const serializer = serializerResult.value
      const serializedChildResult = serializer.serialize(item.content)
      if (!serializedChildResult.success) {
        reportNonFatalError(`scorm suspend data 'protobuf' serialization error for type '${item.name}', ` +
              `it won't be included in saved data`, serializedChildResult.error)

        continue
      }

      // we store the id of the serializer, so we can deserialize using it in the future
      items.push(new ItemProtoMessage({
        id: serializer.key.id,
        content: serializedChildResult.value
      }))
    }

    const payload = {
      items: items
    }

    return ProtobufSerializer.serialize<SuspendDataProtoMessage>(payload, SuspendDataProtoMessage)
  }

  deserialize(content: Uint8Array): Result<ScormSuspendDataState, SerializationApi.DeserializerError> {

    const containerResult = ProtobufSerializer.deserialize<SuspendDataProtoMessage>(content,
                                                                                    SuspendDataProtoMessage)
    if (!containerResult.success) {
      return containerResult
    }

    let outItems = []

    const container = containerResult.value 
    for (let item of container.items) {
      const itemDeserializerResult = this.serializerFactory.resolveById(item.id, 'protobuf')
      if (!itemDeserializerResult.success) {
        reportNonFatalError(`'protobuf' serializer with id '${item.id}' not found, it won't be included in loaded data`)
        continue
      }

      const itemDeserializer = itemDeserializerResult.value
      const itemResult = itemDeserializer.deserialize(item.content)
      if (!itemResult.success) {
        reportNonFatalError(`scorm suspend data 'protobuf' deserialization error for ` +
              `type '${itemDeserializer.key.name.name}', ` +
              `it won't be included in loaded data`, itemResult.error)
        continue
      }

      const name = itemDeserializer.key.name

      outItems.push({
        name: name.name,
        content: itemResult.value
      })
    }

    return ok(outItems)
  }
}

@Type.d('ItemProtoMessage')
class ItemProtoMessage extends Message<ItemProtoMessage> {
  @Field.d(1, 'uint32', 'required')
  public id: number

  @Field.d(2, 'bytes', 'required')
  public content: Uint8Array
}

@Type.d('SuspendDataProtoMessage')
class SuspendDataProtoMessage extends Message<SuspendDataProtoMessage> {

  @Field.d(1, 'uint32', 'optional', 1)
  public schemaVersion: number

  @Field.d(2, ItemProtoMessage, 'repeated')
  public items: ItemProtoMessage[]
}
